import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, DollarSign, TrendingUp, TrendingDown, Download, Calendar, CreditCard, Truck, Receipt, RefreshCw, Percent } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";

const NMI_FEE_PERCENT = 0.029;
const NMI_FEE_FIXED = 0.25;

const AdminRevenuePage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState("30days");
  const [customRange, setCustomRange] = useState({ start: "", end: "" });

  useEffect(() => {
    if (user && user.role !== "admin") navigate("/");
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, productsRes] = await Promise.all([
        axios.get(`${API}/admin/orders?limit=500`, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }),
        axios.get(`${API}/products?limit=500`),
      ]);
      setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : ordersRes.data?.orders || []);
      setProducts(productsRes.data?.products || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load revenue data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [token]);

  const getFilteredOrders = () => {
    const now = new Date();
    let startDate = null;
    switch (dateFilter) {
      case "today": startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); break;
      case "7days": startDate = new Date(now.getTime() - 7 * 86400000); break;
      case "30days": startDate = new Date(now.getTime() - 30 * 86400000); break;
      case "custom": if (customRange.start) startDate = new Date(customRange.start); break;
      default: return orders;
    }
    return orders.filter((o) => {
      if (!o.created_at) return true;
      const d = new Date(o.created_at);
      if (startDate && d < startDate) return false;
      if (dateFilter === "custom" && customRange.end) {
        const end = new Date(customRange.end); end.setHours(23, 59, 59);
        if (d > end) return false;
      }
      return true;
    });
  };

  const filtered = getFilteredOrders();
  const paidOrders = filtered.filter((o) => o.payment_status === "captured" || o.status === "paid" || o.status === "shipped" || o.status === "delivered");
  const refundedOrders = filtered.filter((o) => o.status === "refunded");

  const grossRevenue = paidOrders.reduce((s, o) => s + (o.total || 0), 0);
  const taxCollected = paidOrders.reduce((s, o) => s + (o.tax_amount || 0), 0);
  const shippingCollected = paidOrders.reduce((s, o) => s + (o.shipping_amount || 0), 0);
  const subtotalRevenue = paidOrders.reduce((s, o) => s + (o.subtotal || 0), 0);

  const nmiFees = paidOrders.reduce((s, o) => s + ((o.total || 0) * NMI_FEE_PERCENT + NMI_FEE_FIXED), 0);
  const refundsTotal = refundedOrders.reduce((s, o) => s + (o.refund_amount || o.total || 0), 0);
  const netRevenue = grossRevenue - nmiFees - refundsTotal;

  const productMap = {};
  products.forEach((p) => { productMap[p.product_id] = p; });

  let totalCOGS = 0;
  paidOrders.forEach((order) => {
    (order.items || []).forEach((item) => {
      const product = productMap[item.product_id];
      const cost = product?.cost_price || product?.cost_of_goods || ((item.unit_price || item.price || 0) * 0.6);
      totalCOGS += cost * (item.quantity || 1);
    });
  });

  const estimatedProfit = netRevenue - totalCOGS;
  const profitMargin = grossRevenue > 0 ? (estimatedProfit / grossRevenue) * 100 : 0;

  const exportToCSV = () => {
    const rows = [
      ["Metric", "Amount"],
      ["Gross Revenue", grossRevenue.toFixed(2)],
      ["Subtotal (Products)", subtotalRevenue.toFixed(2)],
      ["Tax Collected (MA 6.25%)", taxCollected.toFixed(2)],
      ["Shipping Collected", shippingCollected.toFixed(2)],
      ["NMI Processing Fees (Est.)", nmiFees.toFixed(2)],
      ["Refunds", refundsTotal.toFixed(2)],
      ["Net Revenue", netRevenue.toFixed(2)],
      ["Cost of Goods (Est.)", totalCOGS.toFixed(2)],
      ["Estimated Profit", estimatedProfit.toFixed(2)],
      ["Profit Margin %", profitMargin.toFixed(1)],
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `revenue_report_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    toast.success("Revenue report exported");
  };

  const MetricCard = ({ icon: Icon, label, value, subtext, color = "primary" }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid={`metric-${label.replace(/\s/g, "-").toLowerCase()}`}>
      <div className="flex items-start justify-between">
        <div className={`p-2.5 rounded-lg ${
          color === "green" ? "bg-green-100" : color === "red" ? "bg-red-100" : color === "blue" ? "bg-blue-100" :
          color === "purple" ? "bg-purple-100" : color === "amber" ? "bg-amber-100" : "bg-primary/10"
        }`}>
          <Icon className={`w-5 h-5 ${
            color === "green" ? "text-green-600" : color === "red" ? "text-red-600" : color === "blue" ? "text-blue-600" :
            color === "purple" ? "text-purple-600" : color === "amber" ? "text-amber-600" : "text-primary"
          }`} />
        </div>
      </div>
      <div className="mt-3">
        <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-0.5">{value}</p>
        {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-revenue-page">
      <div className="bg-white border-b border-gray-200 py-6">
        <div className="container mx-auto px-4">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-primary mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 uppercase tracking-tight">
              Revenue & Profit
            </h1>
            <Button onClick={exportToCSV} variant="outline" data-testid="export-revenue-csv">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Date Filter */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex flex-wrap gap-3 items-center">
            <Calendar className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-600">Period:</span>
            {[
              { value: "today", label: "Today" },
              { value: "7days", label: "7 Days" },
              { value: "30days", label: "30 Days" },
              { value: "all", label: "All Time" },
              { value: "custom", label: "Custom" },
            ].map((o) => (
              <Button key={o.value} size="sm" variant={dateFilter === o.value ? "default" : "outline"} onClick={() => setDateFilter(o.value)}>
                {o.label}
              </Button>
            ))}
            {dateFilter === "custom" && (
              <div className="flex gap-2 ml-4">
                <input type="date" value={customRange.start} onChange={(e) => setCustomRange((p) => ({ ...p, start: e.target.value }))} className="px-3 py-1.5 border rounded text-sm" />
                <span className="text-gray-400 self-center">to</span>
                <input type="date" value={customRange.end} onChange={(e) => setCustomRange((p) => ({ ...p, end: e.target.value }))} className="px-3 py-1.5 border rounded text-sm" />
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <MetricCard icon={DollarSign} label="Gross Revenue" value={`$${grossRevenue.toFixed(2)}`} subtext={`${paidOrders.length} paid orders`} color="green" />
              <MetricCard icon={Receipt} label="Tax Collected" value={`$${taxCollected.toFixed(2)}`} subtext="Massachusetts 6.25%" color="blue" />
              <MetricCard icon={Truck} label="Shipping Collected" value={`$${shippingCollected.toFixed(2)}`} color="purple" />
              <MetricCard icon={CreditCard} label="NMI Fees (Est.)" value={`$${nmiFees.toFixed(2)}`} subtext="~2.9% + $0.25/txn" color="amber" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <MetricCard icon={RefreshCw} label="Refunds" value={`$${refundsTotal.toFixed(2)}`} subtext={`${refundedOrders.length} refunded`} color="red" />
              <MetricCard icon={TrendingUp} label="Net Revenue" value={`$${netRevenue.toFixed(2)}`} subtext="After fees & refunds" color="green" />
              <MetricCard icon={Percent} label="Est. Profit" value={`$${estimatedProfit.toFixed(2)}`} subtext={`${profitMargin.toFixed(1)}% margin`} color={estimatedProfit >= 0 ? "green" : "red"} />
            </div>

            {/* Breakdown Table */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="revenue-breakdown">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <h2 className="font-medium text-gray-900">Revenue Breakdown</h2>
              </div>
              <div className="p-4">
                <table className="w-full">
                  <tbody className="divide-y divide-gray-100">
                    <tr><td className="py-3 text-gray-600">Product Sales (Subtotal)</td><td className="py-3 text-right font-medium text-gray-900">${subtotalRevenue.toFixed(2)}</td></tr>
                    <tr><td className="py-3 text-gray-600">+ Shipping Collected</td><td className="py-3 text-right font-medium text-gray-900">${shippingCollected.toFixed(2)}</td></tr>
                    <tr><td className="py-3 text-gray-600">+ Tax Collected (MA 6.25%)</td><td className="py-3 text-right font-medium text-gray-900">${taxCollected.toFixed(2)}</td></tr>
                    <tr className="bg-gray-50"><td className="py-3 font-medium text-gray-900">= Gross Revenue</td><td className="py-3 text-right font-bold text-green-600">${grossRevenue.toFixed(2)}</td></tr>
                    <tr><td className="py-3 text-gray-600">- NMI Processing Fees (Est.)</td><td className="py-3 text-right font-medium text-red-600">-${nmiFees.toFixed(2)}</td></tr>
                    <tr><td className="py-3 text-gray-600">- Refunds</td><td className="py-3 text-right font-medium text-red-600">-${refundsTotal.toFixed(2)}</td></tr>
                    <tr className="bg-gray-50"><td className="py-3 font-medium text-gray-900">= Net Revenue</td><td className="py-3 text-right font-bold text-gray-900">${netRevenue.toFixed(2)}</td></tr>
                    <tr><td className="py-3 text-gray-600">- Cost of Goods (Est.)</td><td className="py-3 text-right font-medium text-red-600">-${totalCOGS.toFixed(2)}</td></tr>
                    <tr className="bg-green-50">
                      <td className="py-3 font-bold text-gray-900">= Estimated Profit</td>
                      <td className={`py-3 text-right font-bold text-xl ${estimatedProfit >= 0 ? "text-green-600" : "text-red-600"}`}>${estimatedProfit.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="p-4 border-t border-gray-200 bg-amber-50">
                <p className="text-sm text-amber-700">
                  <strong>Note:</strong> Cost of Goods is estimated at 60% of item price when not set. Add "Cost Price" per product for accurate profit.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminRevenuePage;
